import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../../axios";
import usePageTitle from "../../pageTitle";
import AppConfig from "../../../appConfig";

const STORAGE_KEY = "laddar_spin_participant";

function PromotionPage() {
  usePageTitle("Spin & Win");

  const { ref } = useParams();
  const navigate = useNavigate();

  const [promotion, setPromotion] = useState(null);
  const [wheels, setWheels] = useState([]);
  const [participant, setParticipant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const [participantName, setParticipantName] = useState("");
  const [participantEmail, setParticipantEmail] = useState("");
  const [participantPhone, setParticipantPhone] = useState("");

  const showMessage = (message, messageSeverity = "error") => {
    setSeverity(messageSeverity);
    setErrorMsg(message || "An unexpected error occurred.");
  };

  const getSavedParticipant = useCallback(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));

      if (saved && saved.promotion === ref && saved.participant) {
        return saved.participant;
      }
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY);
    }

    return null;
  }, [ref]);

  const saveParticipant = (participantData) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        promotion: ref,
        participant: participantData,
      }),
    );
  };

  const loadPromotion = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const response = await axios.get(`web/tm/spin/${ref}/fetch.json`);

      if (response.data.status !== "success") {
        showMessage(response.data.data?.message);
        return;
      }

      setPromotion(response.data.data.promotion);
      setWheels(response.data.data.wheels || []);
      setParticipant(getSavedParticipant());
    } catch (error) {
      showMessage(
        error.response?.data?.data?.message ||
          "Unable to load this promotion. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [ref, getSavedParticipant]);

  useEffect(() => {
    loadPromotion();
  }, [loadPromotion]);

  const registerParticipant = async () => {
    setSubmitting(true);
    setErrorMsg("");

    try {
      const response = await axios.post(`web/tm/spin/register.json`, {
        reference: ref,
        name: participantName.trim(),
        email_address: participantEmail.trim(),
        phone_number: participantPhone.trim(),
      });

      if (response.data.status !== "success") {
        showMessage(response.data.data?.message);
        return;
      }

      const participantData = response.data.data.participant;

      saveParticipant(participantData);
      setParticipant(participantData);
      setRegistrationOpen(false);

      setParticipantName("");
      setParticipantEmail("");
      setParticipantPhone("");

      showMessage(
        response.data.data.message || "Registration successful!",
        "success",
      );
    } catch (error) {
      showMessage(
        error.response?.data?.data?.message ||
          "Unable to complete registration.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openWheel = (wheel) => {
    if (!participant) {
      setRegistrationOpen(true);
      return;
    }

    navigate(`/tm/spin/wheel/${wheel.reference}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!promotion) {
    return (
      <Box sx={{ minHeight: "100vh", p: 3 }}>
        <Container maxWidth="md">
          <Alert severity="error">
            {errorMsg || "This promotion could not be found."}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 4, md: 7 },
        px: 2,
      }}
    >
      <Container maxWidth="md">
        {errorMsg && (
          <Fade in>
            <Alert
              severity={severity}
              onClose={() => setErrorMsg("")}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {errorMsg}
            </Alert>
          </Fade>
        )}

        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: 3,
              fontSize: 13,
              mb: 2,
            }}
          >
            SPIN & WIN
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: 30, md: 44 },
              fontWeight: 800,
              overflowWrap: "anywhere",
            }}
          >
            {promotion.title}
          </Typography>

          {promotion.description && (
            <Typography
              sx={{
                mt: 2,
                maxWidth: 650,
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              {promotion.description}
            </Typography>
          )}

          <Box
            sx={{
              mt: 5,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {promotion.banner ? (
              <Box
                component="img"
                src={`${AppConfig.api_media_url}${promotion.banner}`}
                alt={promotion.title}
                sx={{
                  display: "block",
                  width: "100%",
                  maxWidth: 440,
                  maxHeight: 340,
                  objectFit: "contain",
                  borderRadius: 3,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 250, sm: 320 },
                  height: { xs: 250, sm: 320 },
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 90,
                    height: 90,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 900,
                      letterSpacing: 1,
                    }}
                  >
                    SPIN
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {!participant && (
            <Button
              variant="contained"
              size="large"
              onClick={() => setRegistrationOpen(true)}
              sx={{
                mt: 4,
                px: 5,
                py: 1.6,
                borderRadius: 2,
                fontWeight: 800,
              }}
            >
              Register Now
            </Button>
          )}

          {participant && (
            <Typography
              sx={{
                mt: 3,
                display: "inline-block",
                borderRadius: 10,
                px: 3,
                py: 1,
              }}
            >
              Welcome, {participant.name}
            </Typography>
          )}
        </Box>

        {participant && (
          <Box>
            {wheels.length === 0 ? (
              <Card>
                <CardContent sx={{ py: 5, textAlign: "center" }}>
                  No active wheels are available at the moment.
                </CardContent>
              </Card>
            ) : (
              <Box>
                <Box mb={2} align="center">
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                    Select A Wheel
                  </Typography>
                </Box>

                <Grid
                  container
                  spacing={3}
                  justifyContent="center"
                  alignItems="stretch"
                >
                  {wheels.map((wheel) => (
                    <Grid item xs={12} sm={6} key={wheel.reference}>
                      <Card
                        sx={{
                          height: "100%",
                          borderRadius: 2,
                          transition: "border-color 0.2s",
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="h6" fontWeight={800}>
                            {wheel.title}
                          </Typography>

                          {wheel.description && (
                            <Typography
                              sx={{
                                mt: 1,
                                lineHeight: 1.7,
                              }}
                            >
                              {wheel.description}
                            </Typography>
                          )}

                          <Button
                            fullWidth
                            variant="contained"
                            onClick={() => openWheel(wheel)}
                            sx={{
                              mt: 3,
                              py: 1.3,
                              fontWeight: 800,
                            }}
                          >
                            Play
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </Container>

      {/* Participant registration dialog */}
      <Dialog
        open={registrationOpen}
        onClose={() => {
          if (!submitting) setRegistrationOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Register to Participate
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 3 }}>
            Enter your details to participate in this promotion.
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Full Name"
              value={participantName}
              onChange={(event) => setParticipantName(event.target.value)}
              fullWidth
              required
              autoComplete="name"
            />

            <TextField
              label="Email Address"
              type="email"
              value={participantEmail}
              onChange={(event) => setParticipantEmail(event.target.value)}
              fullWidth
              required
              autoComplete="email"
            />

            <TextField
              label="Phone Number"
              value={participantPhone}
              onChange={(event) => setParticipantPhone(event.target.value)}
              fullWidth
              required
              autoComplete="tel"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button
            disabled={submitting}
            onClick={() => setRegistrationOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={
              submitting ||
              !participantName.trim() ||
              !participantEmail.trim() ||
              !participantPhone.trim()
            }
            onClick={registerParticipant}
            sx={{ fontWeight: 800 }}
          >
            {submitting ? <CircularProgress size={22} /> : "Register"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PromotionPage;
